import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  Option,
  Stack,
  Alert,
  Chip,
  IconButton,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Divider,
  Grid,
  AspectRatio,
  LinearProgress,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Badge,
} from '@mui/joy';
import Checkbox from '@mui/joy/Checkbox';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useLoading } from '../../context/LoadingContext';
import MainLayout from '../../components/Layout/MainLayout';
import FolderTreeSelector from '../../components/FolderTreeSelector';
import axiosInstance from '../../app/util/axiosConfig';
import signageDomain, { API_ENDPOINTS } from '../../app/util/domain';


const ContentManagementPage = () => {
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [contentStats, setContentStats] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [currentPath, setCurrentPath] = useState([]); // Breadcrumb path
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false);
  const [renameFolderData, setRenameFolderData] = useState({
    id: null,
    folder_name: '',
    description: '',
    color: '#1976d2'
  });
  const [newFolderData, setNewFolderData] = useState({
    folder_name: '',
    description: '',
    color: '#1976d2'
  });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  // Removed activeTab - using folder-based navigation instead
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    content_name: '',
    description: '',
    content_type_id: 1,
    tags: '',
    duration: 10,
    folder_id: null,
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('files'); // 'files' or 'folder'
  const [folderStructure, setFolderStructure] = useState({});
  const [fileTree, setFileTree] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState(null);
  const [isMoveFolderModalOpen, setIsMoveFolderModalOpen] = useState(false);
  const [folderToMove, setFolderToMove] = useState(null);
  const [targetParentFolderId, setTargetParentFolderId] = useState(null);
  const bulkUploadInputRef = useRef(null);

  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { setLoading, isLoading } = useLoading();

  // Debug effect for bulk upload modal
  useEffect(() => {
    if (isBulkUploadModalOpen) {
      console.log('Bulk upload modal opened');
      // Check if input element exists and has correct attributes
      setTimeout(() => {
        const input = document.querySelector('input[type="file"][multiple]');
        if (input) {
          console.log('File input found:', input);
          console.log('Multiple attribute:', input.hasAttribute('multiple'));
          console.log('Accept attribute:', input.getAttribute('accept'));
        } else {
          console.log('File input not found!');
        }
      }, 100);
    }
  }, [isBulkUploadModalOpen]);

  // Global mouse event listeners for drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleContentDragEnd();
      }
    };

    const handleGlobalMouseLeave = () => {
      if (isDragging) {
        handleContentDragEnd();
      }
    };

    const handleKeyDown = (e) => {
      // Ctrl+A or Cmd+A for toggle select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        handleToggleSelectAll();
      }
      // Escape for deselect all
      if (e.key === 'Escape') {
        handleDeselectAll();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('mouseleave', handleGlobalMouseLeave);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mouseleave', handleGlobalMouseLeave);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDragging, dragStart, dragEnd, uploadMode, isBulkUploadModalOpen]);

  // Map content type IDs to display properties
  const getContentTypeIcon = (typeName) => {
    const typeMap = {
      'image': '🖼️',
      'video': '🎬',
      'document': '📄',
      'pdf': '📄',
      'web': '🌐'
    };
    return typeMap[typeName?.toLowerCase()] || '📎';
  };

  const getAcceptedFiles = (typeName) => {
    const acceptMap = {
      'image': 'image/*',
      'video': 'video/*',
      'document': '.pdf,.doc,.docx,.ppt,.pptx',
      'pdf': '.pdf',
      'web': '.html',
    };
    return acceptMap[typeName?.toLowerCase()] || '*';
  };

  // Build absolute URL for server-hosted files
  const buildServerUrl = (relativePath) => {
    if (!relativePath) return null;
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    const trimmed = relativePath.replace(/^\/+/, '');
    return `${signageDomain}/${trimmed}`;
  };

  // Fallback thumbnail (inline SVG)
  const FALLBACK_THUMB =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="200"><rect width="100%" height="100%" fill="#f2f4f7"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" fill="#98a2b3">Preview not available</text></svg>`
    );

  // Prefer thumbnail; fallback to original image if available
  const getImageSrc = (item) => {
    const thumbUrl = buildServerUrl(item.thumbnail);
    if (thumbUrl) return thumbUrl;
    if (item.category === 'image' && item.file_path) {
      return buildServerUrl(item.file_path);
    }
    return null;
  };

  // Load content and content types on mount
  useEffect(() => {
    loadContent();
    loadContentTypes();
    loadContentStats();
    loadFolders();
  }, []);

  // Folder navigation functions
  const navigateToFolder = (folderId) => {
    // Ensure folderId is a number for consistent comparison
    const normalizedFolderId = folderId != null ? Number(folderId) : null;
    setSelectedFolder(normalizedFolderId);
    setSelectedIds([]); // Clear selection when changing folders

    // Update current path when navigating to a folder
    if (normalizedFolderId) {
      const folder = folders.find(f => Number(f.id) === normalizedFolderId);
      if (folder) {
        // Build path from folder hierarchy
        const path = buildPathFromFolder(folder);
        setCurrentPath(path);
      }
    } else {
      setCurrentPath([]);
    }
  };

  const navigateToPath = (pathIndex) => {
    if (pathIndex === -1) {
      // Navigate to root
      setSelectedFolder(null);
      setCurrentPath([]);
    } else {
      // Navigate to specific path level
      const targetPath = currentPath.slice(0, pathIndex + 1);
      setCurrentPath(targetPath);
      setSelectedFolder(targetPath[targetPath.length - 1].id);
    }
    setSelectedIds([]);
  };

  const buildPathFromFolder = (folder) => {
    const path = [folder];
    let current = folder;

    // Find parent folders by looking through all folders - handle type coercion
    while (current.parent_folder_id) {
      const parentId = Number(current.parent_folder_id);
      const parent = folders.find(f => Number(f.id) === parentId);
      if (parent) {
        path.unshift(parent);
        current = parent;
      } else {
        break;
      }
    }

    return path;
  };

  const navigateToRoot = () => {
    setSelectedFolder(null);
    setCurrentPath([]);
    setSelectedIds([]);
  };

  // Create folder function
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderData.folder_name.trim()) {
      setError('Folder name is required');
      return;
    }

    setLoading('createFolder', true);
    setError('');

    try {
      const folderData = {
        folder_name: newFolderData.folder_name.trim(),
        description: newFolderData.description.trim(),
        color: newFolderData.color,
        parent_folder_id: selectedFolder // Create under current folder
      };

      const response = await axiosInstance.post('/api/folders', folderData);

      if (response.data.success) {
        showSuccess('Folder created successfully');
        setIsCreateFolderModalOpen(false);
        setNewFolderData({
          folder_name: '',
          description: '',
          color: '#1976d2'
        });
        await loadFolders(); // Reload folders
      } else {
        showError(response.data.message || 'Failed to create folder');
      }
    } catch (error) {
      console.error('Create folder error:', error);
      showError(error.response?.data?.message || 'Failed to create folder');
    } finally {
      setLoading('createFolder', false);
    }
  };

  const handleRenameFolder = async (e) => {
    e.preventDefault();
    if (!renameFolderData.folder_name.trim()) {
      setError('Folder name is required');
      return;
    }

    setLoading('renameFolder', true);
    setError('');

    try {
      const folderData = {
        folder_name: renameFolderData.folder_name.trim(),
        description: renameFolderData.description.trim(),
        color: renameFolderData.color
      };

      const response = await axiosInstance.put(`/api/folders/${renameFolderData.id}`, folderData);

      if (response.data.success) {
        showSuccess('Folder renamed successfully');
        setIsRenameFolderModalOpen(false);
        setRenameFolderData({
          id: null,
          folder_name: '',
          description: '',
          color: '#1976d2'
        });
        await loadFolders(); // Reload folders
      } else {
        showError(response.data.message || 'Failed to rename folder');
      }
    } catch (error) {
      console.error('Rename folder error:', error);
      showError(error.response?.data?.message || 'Failed to rename folder');
    } finally {
      setLoading('renameFolder', false);
    }
  };

  const openRenameFolderModal = (folder) => {
    setRenameFolderData({
      id: folder.id,
      folder_name: folder.folder_name,
      description: folder.description || '',
      color: folder.color || '#1976d2'
    });
    setIsRenameFolderModalOpen(true);
  };

  const handleOpenMoveFolderModal = (folder) => {
    setFolderToMove(folder);
    setTargetParentFolderId(null);
    setIsMoveFolderModalOpen(true);
  };

  const handleMoveFolder = async () => {
    if (!folderToMove) return;

    setLoading('moveFolder', true);
    setError('');

    try {
      const response = await axiosInstance.post(`/api/folders/${folderToMove.id}/move`, {
        parent_folder_id: targetParentFolderId
      });

      if (response.data.success) {
        showSuccess(`Folder "${folderToMove.folder_name}" moved successfully`);
        setIsMoveFolderModalOpen(false);
        setFolderToMove(null);
        setTargetParentFolderId(null);

        // Reload folders and content
        await loadFolders();
        await loadContent();
        await loadContentStats();

        // If we're currently viewing the moved folder, navigate to its new location
        if (selectedFolder === folderToMove.id) {
          navigateToFolder(targetParentFolderId);
        }
      } else {
        showError(response.data.message || 'Failed to move folder');
      }
    } catch (error) {
      console.error('Move folder error:', error);
      showError(error.response?.data?.message || 'Failed to move folder');
    } finally {
      setLoading('moveFolder', false);
    }
  };

  // Get available destination folders (exclude the folder being moved and its descendants)
  const getAvailableDestinationFolders = () => {
    if (!folderToMove) return folders;

    const excludeIds = new Set([folderToMove.id]);
    
    // Recursively collect all descendant folder IDs
    const collectDescendants = (parentId) => {
      folders.forEach(folder => {
        if (folder.parent_folder_id && Number(folder.parent_folder_id) === Number(parentId)) {
          excludeIds.add(folder.id);
          collectDescendants(folder.id);
        }
      });
    };

    collectDescendants(folderToMove.id);

    // Filter out the folder being moved and its descendants
    return folders.filter(folder => !excludeIds.has(folder.id));
  };

  const handleDeleteFolder = async (folderId) => {
    const normalizedFolderId = Number(folderId);
    const folder = folders.find(f => Number(f.id) === normalizedFolderId);
    if (!folder) return;

    // Check if folder has contents - handle type coercion
    const folderContents = content.filter(item => {
      const itemFolderId = item.folder_id != null ? Number(item.folder_id) : null;
      return itemFolderId === normalizedFolderId;
    });
    const subfolders = folders.filter(f => {
      const folderParentId = f.parent_folder_id != null ? Number(f.parent_folder_id) : null;
      return folderParentId === normalizedFolderId;
    });

    let warningMessage = `Are you sure you want to delete the folder "${folder.folder_name}"?`;

    if (folderContents.length > 0 || subfolders.length > 0) {
      warningMessage += `\n\nThis folder contains:\n`;
      if (folderContents.length > 0) {
        warningMessage += `• ${folderContents.length} content item(s)\n`;
      }
      if (subfolders.length > 0) {
        warningMessage += `• ${subfolders.length} subfolder(s)\n`;
      }
      warningMessage += `\nAll contents and subfolders will be permanently deleted. This action cannot be undone.`;
    } else {
      warningMessage += `\n\nThis action cannot be undone.`;
    }

    if (!window.confirm(warningMessage)) {
      return;
    }

    setLoading('deleteFolder', true);
    try {
      const response = await axiosInstance.delete(`/api/folders/${folderId}`);

      if (response.data.success) {
        showSuccess(`Folder "${folder.folder_name}" deleted successfully`);

        // If we're currently viewing this folder, navigate to parent or root
        const currentSelectedId = selectedFolder != null ? Number(selectedFolder) : null;
        if (currentSelectedId === normalizedFolderId) {
          if (folder.parent_folder_id) {
            navigateToFolder(folder.parent_folder_id);
          } else {
            navigateToRoot();
          }
        }

        await loadFolders(); // Reload folders
        await loadContent(); // Reload content
        await loadContentStats(); // Refresh stats
      } else {
        showError(response.data.message || 'Failed to delete folder');
      }
    } catch (error) {
      console.error('Delete folder error:', error);
      showError(error.response?.data?.message || 'Failed to delete folder');
    } finally {
      setLoading('deleteFolder', false);
    }
  };

  const loadFolders = async () => {
    try {
      // Load flat list for dropdowns and tree view
      const flatResponse = await axiosInstance.get('/api/folders/flat');
      if (flatResponse.data.success) {
        setFolders(flatResponse.data.folders);
      }
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const loadContent = async () => {
    setLoading('loadContent', true);
    try {
      // Load all content without pagination limit to ensure all items are available for filtering
      const response = await axiosInstance.get(API_ENDPOINTS.CONTENT.LIST, {
        params: {
          page: 1,
          limit: 10000, // Increased limit to load all content
          sort_by: 'created_at',
          sort_order: 'DESC'
        }
      });

      if (response.data.success) {
        // Transform the API response to match our frontend format
        const safeParse = (value, fallback) => {
          try {
            if (value === null || value === undefined) return fallback;
            if (typeof value === 'string') return JSON.parse(value);
            if (typeof value === 'object') return value;
            return fallback;
          } catch (_) {
            return fallback;
          }
        };

        const transformedContent = response.data.content.map(item => {
          const metadataObj = safeParse(item.metadata, {});
          const tagsArr = safeParse(item.tags, []);
          // Derive a robust category
          let category = (item.type_name || '').toLowerCase();
          if (!category || category === 'pdf') {
            // Normalize pdf as document
            if (category === 'pdf') category = 'document';
            if (!category) {
              const fp = (item.file_path || '').toLowerCase();
              if (fp.includes('/videos/')) category = 'video';
              else if (fp.includes('/documents/')) category = 'document';
              else if (fp.includes('/images/')) category = 'image';
              else if (fp.includes('/web/')) category = 'web';
              else category = 'unknown';
            }
          }

          return {
            id: item.id,
            title: item.content_name,
            description: metadataObj?.description || '',
            category,
            type_name: item.type_name,
            content_type_id: item.content_type_id,
            size: item.file_size,
            file_path: item.file_path,
            thumbnail: item.thumbnail_path,
            duration: item.duration,
            folder_id: item.folder_id != null ? Number(item.folder_id) : null, // Ensure folder_id is a number or null
            folder_name: item.folder_name,
            folder_path: item.folder_path,
            folder_color: item.folder_color,
            folder_icon: item.folder_icon,
            tags: Array.isArray(tagsArr) ? tagsArr : [],
            status: item.status,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
            createdBy: item.created_by_username,
            usage: item.playlist_usage_count || 0
          };
        });
        setContent(transformedContent);
      } else {
        showError('Failed to load content');
      }
    } catch (error) {
      console.error('Load content error:', error);
      showError(error.message || 'Failed to load content');
    } finally {
      setLoading('loadContent', false);
    }
  };

  const loadContentTypes = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.CONTENT.TYPES);
      if (response.data.success) {
        setContentTypes(response.data.content_types);
      }
    } catch (error) {
      console.error('Load content types error:', error);
      // Set default types if API fails
      setContentTypes([
        { id: 1, type_name: 'Image' },
        { id: 2, type_name: 'Video' },
        { id: 3, type_name: 'Document' },
        { id: 4, type_name: 'Web' }
      ]);
    }
  };

  const loadContentStats = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.CONTENT.STATS);
      if (response.data.success) {
        setContentStats(response.data.statistics);
      }
    } catch (error) {
      console.error('Load content stats error:', error);
    }
  };

  const filterContent = () => {
    if (selectedFolder === null) {
      // Show all content when no folder is selected
      setFilteredContent(content);
    } else {
      // Show content in selected folder - handle type coercion for folder_id comparison
      const selectedFolderId = selectedFolder != null ? Number(selectedFolder) : null;
      setFilteredContent(content.filter(item => {
        const itemFolderId = item.folder_id != null ? Number(item.folder_id) : null;
        return itemFolderId === selectedFolderId;
      }));
    }
  };

  // Get subfolders for current location
  const getCurrentSubfolders = () => {
    if (selectedFolder === null) {
      // Show root level folders (no parent)
      return folders.filter(folder => !folder.parent_folder_id);
    } else {
      // Show subfolders of current folder - handle type coercion
      const selectedFolderId = Number(selectedFolder);
      return folders.filter(folder => {
        const folderParentId = folder.parent_folder_id != null ? Number(folder.parent_folder_id) : null;
        return folderParentId === selectedFolderId;
      });
    }
  };

  // Update filtered content when folder selection changes
  useEffect(() => {
    filterContent();
  }, [content, selectedFolder]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const exists = prev.includes(id);
      if (exists) {
        return prev.filter(x => x !== id);
      }
      return [...prev, id];
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    setLoading('bulkDelete', true);
    try {
      // Check each selected item for layout assignments
      const itemsInLayouts = [];
      for (const id of selectedIds) {
        const layoutCheck = await checkContentInLayouts(id);
        if (layoutCheck.inLayout) {
          itemsInLayouts.push({ id, layoutName: layoutCheck.layoutName });
        }
      }

      if (itemsInLayouts.length > 0) {
        const layoutNames = [...new Set(itemsInLayouts.map(item => item.layoutName))];
        showError(`Cannot delete ${itemsInLayouts.length} item(s). They are currently assigned to layout(s): ${layoutNames.join(', ')}. Please remove them from the layouts first.`);
        return;
      }

      if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} content item(s)? This action cannot be undone.`)) {
        return;
      }

      let success = 0;
      let failed = 0;
      for (const id of selectedIds) {
        try {
          const resp = await axiosInstance.delete(API_ENDPOINTS.CONTENT.DELETE(id));
          if (resp.data.success) success += 1;
          else failed += 1;
        } catch (error) {
          failed += 1;
          console.error(`Failed to delete content ${id}:`, error);
        }
      }

      await loadContent();
      await loadContentStats();
      setSelectedIds([]);

      if (success > 0) {
        showSuccess(`Successfully deleted ${success} item(s)`);
      }
      if (failed > 0) {
        showError(`Failed to delete ${failed} item(s)`);
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      showError('Failed to delete content items. Please try again.');
    } finally {
      setLoading('bulkDelete', false);
    }
  };

  const handleBulkMove = async () => {
    if (selectedIds.length === 0) return;

    setLoading('bulkMove', true);
    try {
      let success = 0;
      let failed = 0;

      for (const id of selectedIds) {
        try {
          const response = await axiosInstance.put(
            API_ENDPOINTS.CONTENT.UPDATE(id),
            { folder_id: targetFolderId }
          );
          if (response.data.success) success += 1;
          else failed += 1;
        } catch (error) {
          failed += 1;
          console.error(`Failed to move content ${id}:`, error);
        }
      }

      await loadContent();
      await loadContentStats();
      setSelectedIds([]);
      setIsMoveModalOpen(false);
      setTargetFolderId(null);

      if (success > 0) {
        showSuccess(`Successfully moved ${success} item(s)`);
      }
      if (failed > 0) {
        showError(`Failed to move ${failed} item(s)`);
      }
    } catch (error) {
      console.error('Bulk move error:', error);
      showError('Failed to move content items. Please try again.');
    } finally {
      setLoading('bulkMove', false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  // Content selection functions
  const handleContentSelect = (contentId) => {
    if (selectedIds.includes(contentId)) {
      setSelectedIds(selectedIds.filter(id => id !== contentId));
    } else {
      setSelectedIds([...selectedIds, contentId]);
    }
  };

  const handleSelectAll = () => {
    setSelectedIds(filteredContent.map(item => item.id));
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.length === filteredContent.length) {
      handleDeselectAll();
    } else {
      handleSelectAll();
    }
  };

  const handleContentDragStart = (contentId, event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
    setDragStart(contentId);
    setDragEnd(contentId);
  };

  const handleContentDragOver = (contentId, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDragging) {
      setDragEnd(contentId);
    }
  };

  const handleContentDragEnd = () => {
    if (isDragging && dragStart && dragEnd) {
      const startIndex = filteredContent.findIndex(item => item.id === dragStart);
      const endIndex = filteredContent.findIndex(item => item.id === dragEnd);

      if (startIndex !== -1 && endIndex !== -1) {
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);
        const rangeIds = filteredContent.slice(minIndex, maxIndex + 1).map(item => item.id);

        setSelectedIds(prev => {
          const newSelection = [...prev];
          rangeIds.forEach(id => {
            if (!newSelection.includes(id)) {
              newSelection.push(id);
            }
          });
          return newSelection;
        });
      }
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  };

  // Function to check for duplicate files
  const checkForDuplicates = (newFiles) => {
    const duplicates = [];
    const uniqueFiles = [];

    newFiles.forEach(file => {
      const fileName = file.name.toLowerCase();
      const existingFile = content.find(item =>
        item.title && item.title.toLowerCase() === fileName.replace(/\.[^/.]+$/, "")
      );

      if (existingFile) {
        duplicates.push({
          file: file,
          existing: existingFile
        });
      } else {
        uniqueFiles.push(file);
      }
    });

    return { duplicates, uniqueFiles };
  };

  // Function to parse folder structure from files
  const parseFolderStructure = (files) => {
    const structure = {};
    const folderPaths = new Set();

    files.forEach(file => {
      // Handle both webkitRelativePath (for folder uploads) and name (for individual files)
      const path = file.webkitRelativePath || file.name;
      const pathParts = path.split('/');

      if (pathParts.length > 1) {
        // Build folder paths
        for (let i = 1; i < pathParts.length; i++) {
          const folderPath = pathParts.slice(0, i).join('/');
          folderPaths.add(folderPath);
        }
      }
    });

    // Create structure object with default values
    folderPaths.forEach(path => {
      const pathParts = path.split('/');
      const folderName = pathParts[pathParts.length - 1];

      structure[path] = {
        name: folderName,
        color: '#1976d2',
        description: ''
      };
    });

    return structure;
  };

  // Function to build tree structure from files
  const buildFileTree = (files) => {
    const tree = {};

    files.forEach(file => {
      const path = file.webkitRelativePath || file.name;
      const pathParts = path.split('/');

      let current = tree;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!current[part]) {
          current[part] = { type: 'folder', children: {}, files: [] };
        }
        current = current[part].children;
      }

      // Add file to the last folder
      const fileName = pathParts[pathParts.length - 1];
      const parentPath = pathParts.slice(0, -1).join('/');
      if (parentPath) {
        let parent = tree;
        for (const part of pathParts.slice(0, -1)) {
          parent = parent[part].children;
        }
        parent[fileName] = { type: 'file', file: file };
      } else {
        tree[fileName] = { type: 'file', file: file };
      }
    });

    return tree;
  };

  // Component to render folder tree
  const FolderTreeDisplay = ({ tree, level = 0 }) => {
    return (
      <Box sx={{ ml: level * 2 }}>
        {Object.entries(tree).map(([name, item]) => (
          <Box key={name} sx={{ mb: 0.5 }}>
            {item.type === 'folder' ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Typography sx={{ fontSize: '14px' }}>📁</Typography>
                  <Typography level="body-sm" sx={{ fontWeight: 'medium' }}>
                    {name}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    ({Object.keys(item.children).length + item.files.length} items)
                  </Typography>
                </Box>
                <FolderTreeDisplay tree={item.children} level={level + 1} />
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, ml: 2 }}>
                <Typography sx={{ fontSize: '12px' }}>🖼️</Typography>
                <Typography level="body-sm">
                  {name}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  ({(item.file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  // Function to find images folder by name
  const findImagesFolder = () => {
    return folders.find(folder =>
      folder.folder_name.toLowerCase() === 'images' ||
      folder.folder_name.toLowerCase() === 'image'
    );
  };

  // Function to get default folder for bulk upload
  const getDefaultBulkUploadFolder = () => {
    const imagesFolder = findImagesFolder();
    return imagesFolder ? imagesFolder.id : null;
  };

  // Function to get folder path string
  const getFolderPath = (folderId) => {
    if (!folderId) return 'Root (No Folder)';

    const normalizedFolderId = Number(folderId);
    const folder = folders.find(f => Number(f.id) === normalizedFolderId);
    if (!folder) return 'Unknown Folder';

    const path = buildPathFromFolder(folder);
    return path.map(f => f.folder_name).join(' / ');
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    console.log('Files selected:', files.length);
    console.log('Files:', files.map(f => ({
      name: f.name,
      webkitRelativePath: f.webkitRelativePath,
      size: f.size,
      type: f.type
    })));

    // Filter only image files
    const imageFiles = files.filter(file => {
      if (!file.name || file.size === 0) return false;

      const fileName = file.name.toLowerCase();
      const isImageByExtension = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName);
      const isImageByMime = file.type.startsWith('image/');
      return isImageByExtension || isImageByMime;
    });

    if (imageFiles.length === 0) {
      setError('No image files found. Please select a folder containing image files.');
      return;
    }

    setSelectedFiles(imageFiles);
    setError('');

    // Parse folder structure if uploading folders
    if (uploadMode === 'folder' && imageFiles.length > 0) {
      const structure = parseFolderStructure(imageFiles);
      const tree = buildFileTree(imageFiles);
      setFolderStructure(structure);
      setFileTree(tree);
      console.log('Parsed folder structure:', structure);
      console.log('Built file tree:', tree);
    }

    // Clear input so same folder can be selected again
    e.target.value = null;
  };


  const handleBulkUpload = async (e) => {
    e.preventDefault();

    // Validate that files are selected
    if (!selectedFiles || selectedFiles.length === 0) {
      setError('Please select at least one image file before uploading.');
      return;
    }

    setLoading('bulkUpload', true);
    setError('');

    try {
      const formDataObj = new FormData();

      // Add all image files with webkitRelativePath preserved
      selectedFiles.forEach(file => {
        console.log('Adding file to FormData:', {
          name: file.name,
          webkitRelativePath: file.webkitRelativePath,
          size: file.size,
          type: file.type
        });
        console.log('FormData filename parameter:', file.webkitRelativePath || file.name);

        // For folder uploads, we need to pass the webkitRelativePath as a separate field
        // since multer doesn't preserve it in the filename parameter
        if (uploadMode === 'folder' && file.webkitRelativePath) {
          // Add the file with original name
          formDataObj.append('files', file);
          // Add the webkitRelativePath as a separate field
          formDataObj.append('filePaths', file.webkitRelativePath);
        } else {
          // For individual files, use the original approach
          formDataObj.append('files', file, file.webkitRelativePath || file.name);
        }
      });

      // Add folder_id if selected (for folder uploads, default to images folder)
      if (uploadMode === 'folder') {
        // For folder uploads, always use images folder as default destination
        const defaultFolder = getDefaultBulkUploadFolder();
        formDataObj.append('parent_folder_id', formData.folder_id || defaultFolder);
      } else {
        // For individual file uploads, use folder_id (not parent_folder_id)
        const folderToUse = formData.folder_id || getDefaultBulkUploadFolder();
        if (folderToUse) {
          formDataObj.append('folder_id', folderToUse);
        }
      }

      // Add common properties with default values
      formDataObj.append('content_type_id', '1'); // Image type
      formDataObj.append('duration', '10'); // Default duration

      // Use default empty tags and description for bulk uploads
      formDataObj.append('tags', JSON.stringify([]));

      const metadata = {
        description: '',
        bulk_upload: true,
        upload_mode: uploadMode
      };
      formDataObj.append('metadata', JSON.stringify(metadata));

      // Add folder structure if uploading folders
      if (uploadMode === 'folder' && Object.keys(folderStructure).length > 0) {
        formDataObj.append('folder_structure', JSON.stringify(folderStructure));
      }

      // Choose the appropriate endpoint based on upload mode
      const endpoint = uploadMode === 'folder' ? '/api/content/bulk-upload-folder' : '/api/content/bulk-upload-images';

      const response = await axiosInstance.post(endpoint, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setBulkUploadProgress(percentCompleted);
        }
      });

      if (response.data.success) {
        let successMessage = `Successfully uploaded ${response.data.successful_uploads} image(s)`;

        if (uploadMode === 'folder' && response.data.created_folders && response.data.created_folders.length > 0) {
          successMessage += ` and created ${response.data.created_folders.length} folder(s)`;
        }

        showSuccess(successMessage);

        if (response.data.errors && response.data.errors.length > 0) {
          const errorMessages = response.data.errors.map(err => `${err.filename}: ${err.error}`).join(', ');
          showError(`Some uploads failed: ${errorMessages}`);
        }

        setIsBulkUploadModalOpen(false);
        resetForm();
        await loadContent();
        await loadContentStats();
        await loadFolders(); // Reload folders to show newly created ones
      } else {
        showError(response.data.message || 'Bulk upload failed');
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      showError(error.response?.data?.message || 'Bulk upload failed. Please try again.');
    } finally {
      setLoading('bulkUpload', false);
      setBulkUploadProgress(0);
    }
  };


  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setLoading('upload', true);
    setError('');

    try {
      let successCount = 0;
      for (const file of selectedFiles) {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        formDataObj.append('content_name', formData.content_name || file.name);
        formDataObj.append('content_type_id', formData.content_type_id);
        formDataObj.append('duration', formData.duration);
        if (formData.folder_id) {
          console.log('Uploading to folder:', formData.folder_id);
          formDataObj.append('folder_id', formData.folder_id);
        } else {
          console.log('No folder selected, uploading to root');
        }

        if (formData.tags) {
          const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(Boolean);
          formDataObj.append('tags', JSON.stringify(tagsArray));
        }

        const metadata = {
          description: formData.description || ''
        };
        formDataObj.append('metadata', JSON.stringify(metadata));

        const response = await axiosInstance.post(API_ENDPOINTS.CONTENT.UPLOAD, formDataObj, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });

        if (response.data.success) {
          successCount += 1;
          // Reload after each upload so UI reflects final server values
          await loadContent();
          await loadContentStats();
        } else {
          showError(response.data.message || `Upload failed for ${file.name}`);
        }
      }

      if (successCount > 0) {
        showSuccess(`Uploaded ${successCount} file(s) successfully`);
        setIsUploadModalOpen(false);
        resetForm();
      }
    } catch (error) {
      console.error('Upload error:', error);
      showError(error.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setLoading('upload', false);
      setUploadProgress(0);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');

    setLoading('editContent', true);
    try {
      const updateData = {
        content_name: formData.content_name,
        duration: formData.duration,
        status: formData.status || selectedContent.status,
        folder_id: formData.folder_id
      };

      if (formData.tags) {
        updateData.tags = formData.tags.split(',').map(tag => tag.trim()).filter(Boolean);
      }

      if (formData.description !== undefined) {
        updateData.metadata = {
          description: formData.description
        };
      }

      const response = await axiosInstance.put(
        API_ENDPOINTS.CONTENT.UPDATE(selectedContent.id),
        updateData
      );

      if (response.data.success) {
        showSuccess('Content updated successfully');
        loadContent(); // Reload content list
        loadContentStats(); // Refresh stats cards
        setIsEditModalOpen(false);
        resetForm();
      } else {
        showError(response.data.message || 'Failed to update content');
      }
    } catch (error) {
      console.error('Edit content error:', error);
      showError(error.response?.data?.message || 'Failed to update content');
    } finally {
      setLoading('editContent', false);
    }
  };

  // Function to check if content is assigned to layouts
  const checkContentInLayouts = async (contentId) => {
    try {
      const response = await axiosInstance.get(`/api/layouts`);
      const layouts = response.data.layouts || [];

      for (const layout of layouts) {
        if (layout.config && typeof layout.config === 'string') {
          try {
            const config = JSON.parse(layout.config);
            if (config.content && Array.isArray(config.content)) {
              const hasContent = config.content.some(item =>
                item.content_id === contentId ||
                (item.content && item.content.id === contentId)
              );
              if (hasContent) {
                return { inLayout: true, layoutName: layout.layout_name };
              }
            }
          } catch (e) {
            console.warn('Error parsing layout config:', e);
          }
        }
      }

      return { inLayout: false };
    } catch (error) {
      console.error('Error checking content in layouts:', error);
      return { inLayout: false };
    }
  };

  const handleDelete = async (contentId) => {
    setLoading('deleteContent', true);
    try {
      // Check if content is assigned to any layout
      const layoutCheck = await checkContentInLayouts(contentId);

      if (layoutCheck.inLayout) {
        showError(`Cannot delete this content. It is currently assigned to the layout "${layoutCheck.layoutName}". Please remove it from the layout first.`);
        return;
      }

      if (window.confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
        const response = await axiosInstance.delete(API_ENDPOINTS.CONTENT.DELETE(contentId));

        if (response.data.success) {
          showSuccess('Content deleted successfully');
          loadContent(); // Reload content list
          loadContentStats(); // Refresh stats cards
        } else {
          showError(response.data.message || 'Failed to delete content');
        }
      }
    } catch (error) {
      console.error('Delete content error:', error);
      if (error.response?.status === 400) {
        showError(error.response.data.message || 'Cannot delete content that is in use');
      } else {
        showError(error.response?.data?.message || 'Failed to delete content');
      }
    } finally {
      setLoading('deleteContent', false);
    }
  };

  const handleStatusChange = async (contentId, newStatus) => {
    setLoading('statusChange', true);
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.CONTENT.UPDATE(contentId),
        { status: newStatus }
      );

      if (response.data.success) {
        showSuccess(`Content ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        loadContent(); // Reload content list
        loadContentStats(); // Refresh stats cards
      } else {
        showError(response.data.message || 'Failed to change content status');
      }
    } catch (error) {
      console.error('Status change error:', error);
      showError(error.response?.data?.message || 'Failed to change content status');
    } finally {
      setLoading('statusChange', false);
    }
  };

  const resetForm = () => {
    setFormData({
      content_name: '',
      description: '',
      content_type_id: contentTypes.length > 0 ? contentTypes[0].id : 1,
      tags: '',
      duration: 10,
      folder_id: getDefaultBulkUploadFolder(), // Default to images folder
    });
    setSelectedFiles([]);
    setSelectedContent(null);
    setError('');
    setUploadProgress(0);
    setBulkUploadProgress(0);
    setUploadMode('files');
    setFolderStructure({});
    setFileTree({});
  };

  const openEditModal = (item) => {
    setSelectedContent(item);
    setFormData({
      content_name: item.title,
      description: item.description || '',
      content_type_id: item.content_type_id,
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : '',
      duration: item.duration,
      status: item.status,
      folder_id: item.folder_id
    });
    setIsEditModalOpen(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getContentStats = () => {
    // Use API stats if available, otherwise calculate from loaded content
    if (contentStats && contentStats.by_type) {
      const byType = contentStats.by_type.reduce((acc, item) => {
        let category = item.type_name?.toLowerCase() || 'unknown';
        if (category === 'pdf') category = 'document';
        acc[category] = (acc[category] || 0) + (item.content_count || 0);
        return acc;
      }, {});

      return {
        total: contentStats.overview?.total_content || content.length,
        images: byType['image'] || 0,
        videos: byType['video'] || 0,
        documents: byType['document'] || byType['pdf'] || 0,
        web: byType['web'] || byType['html'] || 0,
        active: contentStats.overview?.active_content || content.filter(c => c.status === 'active').length,
      };
    }

    // Fallback to calculating from content array
    return {
      total: content.length,
      images: content.filter(c => c.category === 'image').length,
      videos: content.filter(c => c.category === 'video').length,
      documents: content.filter(c => c.category === 'document' || c.type_name?.toLowerCase() === 'pdf').length,
      web: content.filter(c => c.category === 'web' || c.category === 'html').length,
      active: content.filter(c => c.status === 'active').length,
    };
  };

  const stats = getContentStats();

  return (
    <MainLayout currentPage="content">
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography level="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Content Management
            </Typography>
            <Typography level="body-md" sx={{ color: 'neutral.600' }}>
              Manage media files, documents, and web content for digital displays
            </Typography>
          </Box>

          {hasPermission('manage_content') && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => setIsUploadModalOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #eb7a24 0%, #f19b30 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d16000 0%, #e67700 100%)',
                  },
                }}
              >
                📤 Upload Content
              </Button>
              <Button
                onClick={() => {
                  setSelectedFiles([]);
                  setError('');
                  setBulkUploadProgress(0);
                  // Reset form to get default folder
                  resetForm();
                  setIsBulkUploadModalOpen(true);
                }}
                variant="outlined"
                sx={{
                  borderColor: '#eb7a24',
                  color: '#eb7a24',
                  '&:hover': {
                    borderColor: '#d16000',
                    backgroundColor: '#fff5f0',
                  },
                }}
              >
                🖼️ Bulk Upload Images
              </Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={selectedIds.length === 0 || isLoading('bulkMove')}
                loading={isLoading('bulkMove')}
                onClick={() => setIsMoveModalOpen(true)}
              >
                📁 Move Selected ({selectedIds.length})
              </Button>
              <Button
                color="danger"
                variant="solid"
                disabled={selectedIds.length === 0 || isLoading('bulkDelete')}
                loading={isLoading('bulkDelete')}
                onClick={handleBulkDelete}
              >
                🗑️ Delete Selected ({selectedIds.length})
              </Button>
            </Box>
          )}
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid xs={12} sm={6} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography level="title-lg" color="primary">Total</Typography>
                <Typography level="h2" sx={{ color: 'primary.500' }}>{stats.total}</Typography>
                <Typography level="body-sm">Content items</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography level="title-lg">Images</Typography>
                <Typography level="h2">🖼️ {stats.images}</Typography>
                <Typography level="body-sm">Photo files</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography level="title-lg">Videos</Typography>
                <Typography level="h2">🎬 {stats.videos}</Typography>
                <Typography level="body-sm">Video files</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography level="title-lg">Documents</Typography>
                <Typography level="h2">📄 {stats.documents}</Typography>
                <Typography level="body-sm">PDF, DOC files</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography level="title-lg">Web</Typography>
                <Typography level="h2">🌐 {stats.web}</Typography>
                <Typography level="body-sm">Web content</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography level="title-lg" color="success">Active</Typography>
                <Typography level="h2" sx={{ color: 'success.500' }}>{stats.active}</Typography>
                <Typography level="body-sm">Live content</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Folder Tree and Content Layout */}
        <Grid container spacing={2}>
          {/* Folder Tree Sidebar */}
          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: 'fit-content' }}>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  📁 Folders
                </Typography>

                {/* Root/All Content */}
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: selectedFolder === null ? 'primary.50' : 'transparent',
                    border: selectedFolder === null ? '1px solid' : '1px solid transparent',
                    borderColor: selectedFolder === null ? 'primary.200' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'neutral.50'
                    }
                  }}
                  onClick={navigateToRoot}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>🏠</Typography>
                    <Typography level="body-sm" sx={{ flex: 1 }}>
                      All Content
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      {content.length}
                    </Typography>
                  </Box>
                </Box>

                {/* Folder Tree */}
                <FolderTreeSelector
                  folders={folders}
                  selectedFolder={selectedFolder}
                  onSelect={navigateToFolder}
                  maxHeight="400px"
                  showRootOption={false}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Content Area */}
          <Grid xs={12} md={9}>
            <Card variant="outlined">
              {/* Path Navigation */}
              <Box sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'neutral.200',
                backgroundColor: 'neutral.50'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography
                    level="body-sm"
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.600',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => navigateToPath(-1)}
                  >
                    All Content
                  </Typography>
                  {currentPath.map((folder, index) => (
                    <React.Fragment key={folder.id}>
                      <Typography sx={{ color: 'neutral.500' }}>/</Typography>
                      <Typography
                        level="body-sm"
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.600',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigateToPath(index)}
                      >
                        {folder.folder_name}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>

                {/* Current Location Info and Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="title-sm">
                    {selectedFolder === null ? 'All Content' :
                      folders.find(f => Number(f.id) === Number(selectedFolder))?.folder_name || 'Unknown Folder'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                      {filteredContent.length} item(s)
                    </Typography>
                    {hasPermission('manage_content') && (
                      <Button
                        size="sm"
                        variant="outlined"
                        onClick={() => setIsCreateFolderModalOpen(true)}
                        startDecorator="📁"
                      >
                        Create Folder
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Content Grid */}
              <Box sx={{ p: 0 }}>
                {/* Selection Controls */}
                {(filteredContent.length > 0 || getCurrentSubfolders().length > 0) && (
                  <Box sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'neutral.50'
                  }}>
                    <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                      {selectedIds.length} of {filteredContent.length} items selected
                      <br />
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        Tip: Use Ctrl+A to toggle selection, Escape to deselect all
                      </Typography>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        disabled={selectedIds.length === 0}
                        onClick={() => setIsMoveModalOpen(true)}
                      >
                        📁 Move ({selectedIds.length})
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedIds.length === filteredContent.length ? "solid" : "outlined"}
                        color={selectedIds.length === filteredContent.length ? "primary" : "neutral"}
                        onClick={handleToggleSelectAll}
                      >
                        {selectedIds.length === filteredContent.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Content Grid */}
                <Grid container spacing={3} sx={{ p: 3 }}>
                  {/* Subfolders */}
                  {getCurrentSubfolders().map((folder) => (
                    <Grid key={`folder-${folder.id}`} xs={12} sm={6} md={4} lg={3}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.300',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }
                        }}
                        onClick={() => navigateToFolder(folder.id)}
                      >
                        <AspectRatio ratio="16/9">
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'neutral.50',
                            border: '2px dashed',
                            borderColor: 'neutral.300',
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <Typography level="h2" sx={{ mb: 1 }}>
                              📁
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                              Folder
                            </Typography>
                          </Box>
                        </AspectRatio>

                        <CardContent sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography level="title-md" sx={{ flex: 1, mr: 1 }} noWrap>
                              {folder.folder_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: folder.color || '#1976d2'
                              }} />
                              {hasPermission('manage_content') && (
                                <Dropdown>
                                  <MenuButton
                                    slots={{ root: IconButton }}
                                    slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    ⋮
                                  </MenuButton>
                                  <Menu>
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openRenameFolderModal(folder);
                                      }}
                                    >
                                      ✏️ Rename Folder
                                    </MenuItem>
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenMoveFolderModal(folder);
                                      }}
                                    >
                                      📁 Move Folder
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFolder(folder.id);
                                      }}
                                      sx={{ color: 'danger.500' }}
                                    >
                                      🗑️ Delete Folder
                                    </MenuItem>
                                  </Menu>
                                </Dropdown>
                              )}
                            </Box>
                          </Box>

                          <Typography level="body-sm" sx={{ mb: 1, color: 'neutral.600' }} noWrap>
                            {folder.description || 'No description'}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            <Chip size="sm" variant="soft" color="primary">
                              folder
                            </Chip>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                              {folder.content_count || 0} items
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                              Click to open
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}

                  {/* Content Items */}
                  {filteredContent.map((item) => (
                    <Grid key={item.id} xs={12} sm={6} md={4} lg={3}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderColor: selectedIds.includes(item.id) ? 'danger.400' : undefined,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          userSelect: 'none', // Prevent text selection
                          WebkitUserSelect: 'none', // Safari
                          MozUserSelect: 'none', // Firefox
                          msUserSelect: 'none', // IE/Edge
                          '&:hover': {
                            borderColor: selectedIds.includes(item.id) ? 'danger.500' : 'primary.300',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          },
                          '& *': {
                            userSelect: 'none', // Prevent text selection on all child elements
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            pointerEvents: 'none' // Prevent pointer events on child elements
                          }
                        }}
                        onClick={() => handleContentSelect(item.id)}
                      >
                        <AspectRatio ratio="16/9">
                          {(() => {
                            const imageSrc = getImageSrc(item) || FALLBACK_THUMB;
                            if (item.category === 'image') {
                              return (
                                <img
                                  src={imageSrc}
                                  alt={item.title}
                                  style={{ objectFit: 'contain' }}
                                />
                              );
                            }
                            if (item.category === 'video') {
                              return (
                                <img
                                  src={imageSrc}
                                  alt={item.title}
                                  style={{ objectFit: 'contain' }}
                                />
                              );
                            }
                            if (item.category === 'document' || item.category === 'pdf') {
                              return (
                                <img
                                  src={imageSrc}
                                  alt={item.title}
                                  style={{ objectFit: 'contain' }}
                                />
                              );
                            }
                            return (
                              <img
                                src={imageSrc}
                                alt={item.title}
                                style={{ objectFit: 'contain' }}
                              />
                            );
                          })()}
                        </AspectRatio>

                        <CardContent sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography level="title-md" sx={{ flex: 1, mr: 1 }} noWrap>
                              {item.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {selectedIds.includes(item.id) && (
                                <Box sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  backgroundColor: 'danger.500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  ✓
                                </Box>
                              )}
                              <Dropdown>
                                <MenuButton
                                  slots={{ root: IconButton }}
                                  slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                                >
                                  ⋮
                                </MenuButton>
                                <Menu>
                                  <MenuItem onClick={() => openEditModal(item)}>
                                    ✏️ Edit
                                  </MenuItem>
                                  <MenuItem onClick={() => handleStatusChange(item.id, item.status === 'active' ? 'inactive' : 'active')}>
                                    {item.status === 'active' ? '🚫 Deactivate' : '✅ Activate'}
                                  </MenuItem>
                                  <Divider />
                                  <MenuItem
                                    onClick={() => handleDelete(item.id)}
                                    sx={{ color: 'danger.500' }}
                                  >
                                    🗑️ Delete
                                  </MenuItem>
                                </Menu>
                              </Dropdown>
                            </Box>
                          </Box>

                          <Typography level="body-sm" sx={{ mb: 1, color: 'neutral.600' }} noWrap>
                            {item.description}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            <Chip size="sm" variant="soft" color={item.status === 'active' ? 'success' : 'neutral'}>
                              {item.status}
                            </Chip>
                            {item.folder_name && (
                              <Chip
                                size="sm"
                                variant="outlined"
                                startDecorator={
                                  <Box sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: item.folder_color || '#1976d2'
                                  }} />
                                }
                              >
                                {item.folder_name}
                              </Chip>
                            )}
                            {item.tags.slice(0, 2).map((tag) => (
                              <Chip key={tag} size="sm" variant="outlined">
                                {tag}
                              </Chip>
                            ))}
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                              {formatFileSize(item.size)}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                              Used {item.usage}x
                            </Typography>
                          </Box>

                          <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
                            {item.dimensions} • {item.duration > 0 ? `${item.duration}s` : 'Live'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Upload Modal */}
        <Modal open={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)}>
          <ModalDialog sx={{
            minWidth: 600,
            maxWidth: 800,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Upload Content
            </Typography>

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
              <form id="upload-form" onSubmit={handleUpload}>
                <Stack spacing={3}>
                  {error && (
                    <Alert color="danger" variant="soft">
                      {error}
                    </Alert>
                  )}

                  <FormControl>
                    <FormLabel>Content Type</FormLabel>
                    <Select
                      value={formData.content_type_id}
                      onChange={(e, value) => setFormData({ ...formData, content_type_id: value })}
                    >
                      {contentTypes.map((type) => (
                        <Option key={type.id} value={type.id}>
                          {getContentTypeIcon(type.type_name)} {type.type_name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Folder (Optional)</FormLabel>
                    <FolderTreeSelector
                      folders={folders}
                      selectedFolder={formData.folder_id}
                      onSelect={(folderId) => {
                        console.log('Folder selected:', folderId);
                        setFormData({ ...formData, folder_id: folderId });
                      }}
                      maxHeight="150px"
                      showRootOption={true}
                      rootOptionLabel="No Folder (Root)"
                    />
                    {formData.folder_id && (
                      <Typography level="body-xs" sx={{ color: 'success.600', mt: 1 }}>
                        Selected folder: {getFolderPath(formData.folder_id)}
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Select Files</FormLabel>
                    <Input
                      type="file"
                      multiple
                      accept={getAcceptedFiles(contentTypes.find(t => t.id === formData.content_type_id)?.type_name)}
                      onChange={handleFileSelect}
                      required
                    />
                    {selectedFiles && selectedFiles.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                          Selected: {selectedFiles.map(f => f.name).join(', ')}
                        </Typography>
                      </Box>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Content Name</FormLabel>
                    <Input
                      placeholder="Enter content name"
                      value={formData.content_name}
                      onChange={(e) => setFormData({ ...formData, content_name: e.target.value })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      placeholder="Enter content description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      minRows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Tags (comma-separated)</FormLabel>
                    <Input
                      placeholder="e.g., logo, branding, company"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Display Duration (seconds)</FormLabel>
                    <Input
                      type="number"
                      min="1"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 10 })}
                    />
                  </FormControl>

                  {uploadProgress > 0 && (
                    <Box>
                      <Typography level="body-sm" sx={{ mb: 1 }}>
                        Upload Progress: {uploadProgress}%
                      </Typography>
                      <LinearProgress determinate value={uploadProgress} />
                    </Box>
                  )}
                </Stack>
              </form>
            </Box>

            {/* Fixed Bottom Buttons */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'neutral.200' }}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setIsUploadModalOpen(false)}
                  sx={{ flex: 1 }}
                  disabled={isLoading('upload')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="upload-form"
                  loading={isLoading('upload')}
                  sx={{ flex: 1 }}
                >
                  Upload
                </Button>
              </Stack>
            </Box>
          </ModalDialog>
        </Modal>

        {/* Bulk Upload Images Modal */}
        <Modal open={isBulkUploadModalOpen} onClose={() => {
          setIsBulkUploadModalOpen(false);
          setSelectedFiles([]);
          setError('');
          setBulkUploadProgress(0);
          setUploadMode('files');
          setFolderStructure({});
          setFileTree({});
        }}>
          <ModalDialog sx={{ minWidth: 500, maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              🖼️ Bulk Upload Images
            </Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 2 }}>
              Upload multiple images at once. Choose between individual files or entire folder structure.
            </Typography>

            {/* Upload Mode Selection */}
            <FormControl>
              <FormLabel>Upload Mode</FormLabel>
              <Select
                value={uploadMode}
                onChange={(e, value) => {
                  setUploadMode(value);
                  setSelectedFiles([]);
                  setFolderStructure({});
                  setFileTree({});
                  setDuplicateFiles([]);
                  setError(''); // Clear any previous errors
                }}
              >
                <Option value="files">📁 Individual Files</Option>
                <Option value="folder">📂 Folder Structure</Option>
              </Select>
              <FormHelperText>
                {uploadMode === 'files'
                  ? 'Upload individual image files with the same properties'
                  : 'Upload entire folder structure and create folders automatically'
                }
              </FormHelperText>
            </FormControl>

            {uploadMode === 'folder' && (
              <Alert color="info" variant="soft">
                <Typography level="body-sm">
                  <strong>📂 Folder Upload:</strong> Click the button below to select a folder.
                  Navigate to your folder, click "Open", then select all image files and click "Open" again.
                  The folder structure will be recreated automatically.
                </Typography>
              </Alert>
            )}

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
              <form id="bulk-upload-form" onSubmit={handleBulkUpload}>
                <Stack spacing={3}>
                  {error && (
                    <Alert color="danger" variant="soft">
                      {error}
                    </Alert>
                  )}

                  <FormControl>
                    <FormLabel>Select Images</FormLabel>

                    <Button
                      variant="outlined"
                      onClick={() => {
                        console.log('Button clicked, uploadMode:', uploadMode);
                        console.log('Input ref:', bulkUploadInputRef.current);
                        if (bulkUploadInputRef.current) {
                          console.log('Input attributes:', {
                            webkitdirectory: bulkUploadInputRef.current.getAttribute('webkitdirectory'),
                            directory: bulkUploadInputRef.current.getAttribute('directory'),
                            mozdirectory: bulkUploadInputRef.current.getAttribute('mozdirectory'),
                            multiple: bulkUploadInputRef.current.getAttribute('multiple')
                          });
                          bulkUploadInputRef.current.click();
                        }
                      }}
                      disabled={isLoading('bulkUpload')}
                      sx={{ mb: 2 }}
                    >
                      {uploadMode === 'folder' ? '📂 Select Folder' : '📁 Select Multiple Images'}
                    </Button>

                    {/* Hidden File Input */}
                    <input
                      ref={bulkUploadInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      webkitdirectory={uploadMode === 'folder' ? 'true' : undefined}
                      directory={uploadMode === 'folder' ? 'true' : undefined}
                      mozdirectory={uploadMode === 'folder' ? 'true' : undefined}
                      multiple
                      onChange={handleBulkFileSelect}
                    />

                    <FormHelperText>
                      {uploadMode === 'folder'
                        ? 'Select a folder containing image files. The folder structure will be recreated automatically.'
                        : 'Select multiple image files to upload with the same properties.'
                      }
                    </FormHelperText>
                  </FormControl>

                  {selectedFiles.length > 0 && (
                    <Alert color="info" variant="soft">
                      <Typography level="body-sm" sx={{ mb: 2 }}>
                        Selected {selectedFiles.length} file(s):
                      </Typography>
                      {uploadMode === 'folder' ? (
                        <Box sx={{
                          backgroundColor: 'neutral.50',
                          borderRadius: '8px',
                          p: 2,
                          border: '1px solid',
                          borderColor: 'neutral.200',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          <Typography level="body-sm" sx={{ fontWeight: 'medium', mb: 1 }}>
                            📂 Folder Structure Preview:
                          </Typography>
                          <FolderTreeDisplay tree={fileTree} />
                          <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 1, fontStyle: 'italic' }}>
                            Folders will be created automatically with this structure.
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {selectedFiles.map((file, index) => (
                            <Typography key={index} level="body-sm" sx={{ mb: 0.5 }}>
                              • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Alert>
                  )}


                  <FormControl>
                    <FormLabel>Destination Folder</FormLabel>
                    <FolderTreeSelector
                      folders={folders}
                      selectedFolder={formData.folder_id || getDefaultBulkUploadFolder()}
                      onSelect={(folderId) => setFormData({ ...formData, folder_id: folderId })}
                      maxHeight="150px"
                      showRootOption={true}
                      rootOptionLabel="No Folder (Root)"
                    />
                    <Typography level="body-xs" sx={{ color: 'success.600', mt: 1 }}>
                      Upload to: {getFolderPath(formData.folder_id || getDefaultBulkUploadFolder())}
                    </Typography>
                  </FormControl>

                  {bulkUploadProgress > 0 && (
                    <Alert color="info" variant="soft">
                      <Typography level="body-sm">
                        Uploading... {bulkUploadProgress}%
                      </Typography>
                      <LinearProgress value={bulkUploadProgress} sx={{ mt: 1 }} />
                    </Alert>
                  )}
                </Stack>
              </form>
            </Box>

            {/* Fixed Bottom Buttons */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'neutral.200' }}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsBulkUploadModalOpen(false);
                    setSelectedFiles([]);
                    setError('');
                    setBulkUploadProgress(0);
                    setUploadMode('files');
                    setFolderStructure({});
                    setFileTree({});
                  }}
                  sx={{ flex: 1 }}
                  disabled={isLoading('bulkUpload')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="bulk-upload-form"
                  loading={isLoading('bulkUpload')}
                  sx={{ flex: 1 }}
                  disabled={selectedFiles.length === 0}
                >
                  {uploadMode === 'folder'
                    ? `Upload Folder Structure (${selectedFiles.length} files)`
                    : `Upload ${selectedFiles.length} Image(s)`
                  }
                </Button>
              </Stack>
            </Box>
          </ModalDialog>
        </Modal>

        {/* Edit Modal */}
        <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <ModalDialog sx={{ minWidth: 500, maxWidth: 600 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Edit Content
            </Typography>

            <form onSubmit={handleEdit}>
              <Stack spacing={3}>
                {error && (
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    placeholder="Enter content title"
                    value={formData.content_name}
                    onChange={(e) => setFormData({ ...formData, content_name: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="Enter content description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    minRows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Tags (comma-separated)</FormLabel>
                  <Input
                    placeholder="e.g., logo, branding, company"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Folder</FormLabel>
                  <FolderTreeSelector
                    folders={folders}
                    selectedFolder={formData.folder_id}
                    onSelect={(folderId) => setFormData({ ...formData, folder_id: folderId })}
                    maxHeight="150px"
                    showRootOption={true}
                    rootOptionLabel="No Folder (Root)"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Display Duration (seconds)</FormLabel>
                  <Input
                    type="number"
                    min="1"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 10 })}
                  />
                </FormControl>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditModalOpen(false)}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isLoading('editContent')}
                    sx={{ flex: 1 }}
                  >
                    Update
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

        {/* Create Folder Modal */}
        <Modal open={isCreateFolderModalOpen} onClose={() => setIsCreateFolderModalOpen(false)}>
          <ModalDialog sx={{ minWidth: 400, maxWidth: 500 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Create New Folder
            </Typography>

            <form onSubmit={handleCreateFolder}>
              <Stack spacing={3}>
                {error && (
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Folder Name</FormLabel>
                  <Input
                    placeholder="Enter folder name"
                    value={newFolderData.folder_name}
                    onChange={(e) => setNewFolderData({ ...newFolderData, folder_name: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description (Optional)</FormLabel>
                  <Textarea
                    placeholder="Enter folder description"
                    value={newFolderData.description}
                    onChange={(e) => setNewFolderData({ ...newFolderData, description: e.target.value })}
                    minRows={2}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Folder Color</FormLabel>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      '#1976d2', '#2e7d32', '#d32f2f', '#7b1fa2',
                      '#f57c00', '#795548', '#607d8b', '#e91e63'
                    ].map((color) => (
                      <Box
                        key={color}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: color,
                          cursor: 'pointer',
                          border: newFolderData.color === color ? '3px solid #000' : '2px solid #fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          }
                        }}
                        onClick={() => setNewFolderData({ ...newFolderData, color })}
                      />
                    ))}
                  </Box>
                </FormControl>

                <Alert color="info" variant="soft">
                  <Typography level="body-sm">
                    This folder will be created in: <strong>
                      {selectedFolder === null ? 'All Content' :
                        folders.find(f => Number(f.id) === Number(selectedFolder))?.folder_name || 'Unknown Folder'}
                    </strong>
                  </Typography>
                </Alert>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsCreateFolderModalOpen(false)}
                    sx={{ flex: 1 }}
                    disabled={isLoading('createFolder')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isLoading('createFolder')}
                    sx={{ flex: 1 }}
                  >
                    Create Folder
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

        {/* Rename Folder Modal */}
        <Modal open={isRenameFolderModalOpen} onClose={() => setIsRenameFolderModalOpen(false)}>
          <ModalDialog sx={{ minWidth: 400, maxWidth: 500 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Rename Folder
            </Typography>

            <form onSubmit={handleRenameFolder}>
              <Stack spacing={3}>
                {error && (
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Folder Name</FormLabel>
                  <Input
                    placeholder="Enter folder name"
                    value={renameFolderData.folder_name}
                    onChange={(e) => setRenameFolderData({ ...renameFolderData, folder_name: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description (Optional)</FormLabel>
                  <Textarea
                    placeholder="Enter folder description"
                    value={renameFolderData.description}
                    onChange={(e) => setRenameFolderData({ ...renameFolderData, description: e.target.value })}
                    minRows={2}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Folder Color</FormLabel>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      '#1976d2', '#2e7d32', '#d32f2f', '#7b1fa2',
                      '#f57c00', '#795548', '#607d8b', '#e91e63'
                    ].map((color) => (
                      <Box
                        key={color}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: color,
                          cursor: 'pointer',
                          border: renameFolderData.color === color ? '3px solid #000' : '2px solid #fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          }
                        }}
                        onClick={() => setRenameFolderData({ ...renameFolderData, color })}
                      />
                    ))}
                  </Box>
                </FormControl>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsRenameFolderModalOpen(false)}
                    sx={{ flex: 1 }}
                    disabled={isLoading('renameFolder')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isLoading('renameFolder')}
                    sx={{ flex: 1 }}
                  >
                    Rename Folder
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

        {/* Move Folder Modal */}
        <Modal open={isMoveFolderModalOpen} onClose={() => {
          setIsMoveFolderModalOpen(false);
          setFolderToMove(null);
          setTargetParentFolderId(null);
        }}>
          <ModalDialog sx={{ minWidth: 400, maxWidth: 500 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Move Folder
            </Typography>

            <Stack spacing={3}>
              {error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}

              {folderToMove && (
                <Alert color="info" variant="soft">
                  <Typography level="body-sm">
                    Moving folder: <strong>{folderToMove.folder_name}</strong>
                    <br />
                    <Typography level="body-xs" sx={{ mt: 0.5 }}>
                      All subfolders will be moved along with this folder.
                    </Typography>
                  </Typography>
                </Alert>
              )}

              <FormControl>
                <FormLabel>Destination Folder</FormLabel>
                <FolderTreeSelector
                  folders={getAvailableDestinationFolders()}
                  selectedFolder={targetParentFolderId}
                  onSelect={setTargetParentFolderId}
                  maxHeight="200px"
                  showRootOption={true}
                  rootOptionLabel="Root (No Folder)"
                />
                <Typography level="body-xs" sx={{ color: 'success.600', mt: 1 }}>
                  Move to: {targetParentFolderId === null ? 'Root' : folders.find(f => Number(f.id) === Number(targetParentFolderId))?.folder_name || 'Selected Folder'}
                </Typography>
              </FormControl>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsMoveFolderModalOpen(false);
                    setFolderToMove(null);
                    setTargetParentFolderId(null);
                  }}
                  sx={{ flex: 1 }}
                  disabled={isLoading('moveFolder')}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMoveFolder}
                  loading={isLoading('moveFolder')}
                  sx={{ flex: 1 }}
                  disabled={!folderToMove}
                >
                  Move Folder
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>

        {/* Move Folder Modal */}
        <Modal open={isMoveFolderModalOpen} onClose={() => {
          setIsMoveFolderModalOpen(false);
          setFolderToMove(null);
          setTargetParentFolderId(null);
        }}>
          <ModalDialog sx={{ minWidth: 400, maxWidth: 500 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Move Folder
            </Typography>

            <Stack spacing={3}>
              {error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}

              {folderToMove && (
                <Alert color="info" variant="soft">
                  <Typography level="body-sm">
                    Moving folder: <strong>{folderToMove.folder_name}</strong>
                    <br />
                    <Typography level="body-xs" sx={{ mt: 0.5 }}>
                      All subfolders will be moved along with this folder.
                    </Typography>
                  </Typography>
                </Alert>
              )}

              <FormControl>
                <FormLabel>Destination Folder</FormLabel>
                <FolderTreeSelector
                  folders={getAvailableDestinationFolders()}
                  selectedFolder={targetParentFolderId}
                  onSelect={setTargetParentFolderId}
                  maxHeight="200px"
                  showRootOption={true}
                  rootOptionLabel="Root (No Folder)"
                />
                <Typography level="body-xs" sx={{ color: 'success.600', mt: 1 }}>
                  Move to: {targetParentFolderId === null ? 'Root' : folders.find(f => Number(f.id) === Number(targetParentFolderId))?.folder_name || 'Selected Folder'}
                </Typography>
              </FormControl>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsMoveFolderModalOpen(false);
                    setFolderToMove(null);
                    setTargetParentFolderId(null);
                  }}
                  sx={{ flex: 1 }}
                  disabled={isLoading('moveFolder')}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMoveFolder}
                  loading={isLoading('moveFolder')}
                  sx={{ flex: 1 }}
                  disabled={!folderToMove}
                >
                  Move Folder
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>

        {/* Move Content Modal */}
        <Modal open={isMoveModalOpen} onClose={() => setIsMoveModalOpen(false)}>
          <ModalDialog sx={{ minWidth: 400, maxWidth: 500 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Move Content to Folder
            </Typography>

            <Stack spacing={3}>
              {error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}

              <Alert color="info" variant="soft">
                <Typography level="body-sm">
                  Moving <strong>{selectedIds.length}</strong> selected item(s) to: <strong>
                    {targetFolderId === null ? 'Root (No Folder)' :
                      folders.find(f => Number(f.id) === Number(targetFolderId))?.folder_name || 'No folder selected'}
                  </strong>
                </Typography>
              </Alert>

              <FormControl>
                <FormLabel>Destination Folder</FormLabel>
                <FolderTreeSelector
                  folders={folders}
                  selectedFolder={targetFolderId}
                  onSelect={setTargetFolderId}
                  maxHeight="150px"
                  showRootOption={true}
                  rootOptionLabel="No Folder (Root)"
                />
              </FormControl>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsMoveModalOpen(false);
                    setTargetFolderId(null);
                  }}
                  sx={{ flex: 1 }}
                  disabled={isLoading('bulkMove')}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkMove}
                  loading={isLoading('bulkMove')}
                  sx={{ flex: 1 }}
                  disabled={selectedIds.length === 0}
                >
                  Move {selectedIds.length} Item(s) to {targetFolderId === null ? 'Root' : folders.find(f => Number(f.id) === Number(targetFolderId))?.folder_name || 'Selected Folder'}
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>
      </Box>
    </MainLayout>
  );
};

export default ContentManagementPage;